import { useState, useEffect } from 'react';
import { useCompany } from '../components/Layout';
import api from '../utils/api';

const emptyClient = {
  name: '', contact_person: '', email: '', phone: '',
  address: '', city: '', state: '', country: 'India', pincode: '',
  gstin: '', currency: 'INR', notes: ''
};

export default function ClientsPage() {
  const { activeCompany } = useCompany();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    if (!activeCompany) return;
    api.get(`/clients?company_id=${activeCompany.id}`).then(r => setClients(r.data));
  };

  useEffect(load, [activeCompany]);

  const openNew = () => { setEditing(null); setForm({ ...emptyClient }); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...c }); setModal(true); };
  const closeModal = () => { setModal(false); setMsg(''); };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) return setMsg('Client name is required');
    setSaving(true); setMsg('');
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
      } else {
        await api.post('/clients', { ...form, company_id: activeCompany.id });
      }
      load(); closeModal();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Cannot delete');
    }
  };

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Clients</h2>
          <p>{activeCompany?.name} · {clients.length} client{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Client</button>
      </div>

      <div className="filter-bar">
        <input className="search-input" placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="card" style={{ padding: 0 }}>
        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="icon">👥</div>
            <p>{search ? 'No clients match your search' : 'No clients yet. Add your first client!'}</p>
            {!search && <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={openNew}>+ Add Client</button>}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Email</th>
                <th>City</th>
                <th>GSTIN</th>
                <th>Currency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td style={{ color: '#666' }}>{c.contact_person || '—'}</td>
                  <td style={{ color: '#666', fontSize: 13 }}>{c.email || '—'}</td>
                  <td style={{ color: '#666' }}>{c.city || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#888' }}>{c.gstin || '—'}</td>
                  <td><span className={`badge badge-${c.currency.toLowerCase()}`}>{c.currency}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Edit Client' : 'Add New Client'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {msg && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 7, marginBottom: 16, fontSize: 13 }}>{msg}</div>}

              <div className="form-grid form-grid-2" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Company / Client Name *</label>
                  <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Widget Corp." />
                </div>
                <div className="form-group">
                  <label>Contact Person</label>
                  <input className="form-control" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} placeholder="e.g. Steve Widget" />
                </div>
              </div>

              <div className="form-grid form-grid-2" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="client@example.com" />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 9800000000" />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Address</label>
                <input className="form-control" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Street address" />
              </div>

              <div className="form-grid form-grid-3" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>City</label>
                  <input className="form-control" value={form.city} onChange={e => set('city', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input className="form-control" value={form.state} onChange={e => set('state', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Pincode</label>
                  <input className="form-control" value={form.pincode} onChange={e => set('pincode', e.target.value)} />
                </div>
              </div>

              <div className="form-grid form-grid-2" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Country</label>
                  <input className="form-control" value={form.country} onChange={e => set('country', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Default Currency</label>
                  <div className="currency-toggle">
                    <button type="button" className={form.currency === 'INR' ? 'active-inr' : ''} onClick={() => set('currency', 'INR')}>₹ INR</button>
                    <button type="button" className={form.currency === 'USD' ? 'active-usd' : ''} onClick={() => set('currency', 'USD')}>$ USD</button>
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>GSTIN {form.currency === 'USD' && <span style={{ color: '#aaa', fontWeight: 400 }}>(optional for USD clients)</span>}</label>
                <input className="form-control" value={form.gstin} onChange={e => set('gstin', e.target.value)} placeholder="e.g. 07AAACR5055K1Z5" style={{ fontFamily: 'monospace' }} />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes about this client..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Client' : 'Add Client'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
